import {
  agreementsApi,
  setMdnData,
  setNamesData,
  setNricData,
  verifyNricApi,
} from "./actions";
import { addJourneyMessages, sleep } from "../../../helpers/addJourneyMessages";
import createMessage from "../../../helpers/createMessage";
import {
  API_PROGRESS,
  COMPONENTS,
  PROGRESS_DATA,
  ACTIVITY,
  INCIDENT_PATH_TYPE,
  CLIENT
} from "../../../helpers/constants";
import {
  updateApiProgress,
  clearApiProgress,
  updateFlowProgress,
  serviceUnavailable,
} from "../../../actions/flowWindow";
import {
  determineRequest,
  cancelServiceRequest,
} from "../../../actions/serviceRequestApi";
import { getMaskedNric, isEmpty } from "../../../helpers/formatUtils";
import updateVisitor from "../../Appsync/updateVisitor";
import { udpateChatInputVisibility } from "../../Appsync/state/operators";
import { setChatReason } from "../../Appsync/state/reducers";

//async action creator or thunk

export const validateMdn = (mdn) => async (dispatch, getStore) => {
  // Before API Call
  const {
    CacheId,
    UserName = "User ",
  } = getStore().session.sessionData.Configurations;
  const client = getStore().app.client;
  const isSingtel = client === CLIENT.SINGTEL;
  const { agreement } = getStore().validation;
  const agreementFailedAttempt = agreement
    ? agreement.AgreementFailedAttempt
    : 0;
  dispatch(addJourneyMessages([createMessage("TEXT", "user", mdn)]));
  await dispatch(setMdnData(mdn));
  await dispatch(
    updateApiProgress(API_PROGRESS.ASK_MDN, 40, COMPONENTS.ASK_MDN, 0)
  );

  // API Call
  const response = await dispatch(
    agreementsApi({
      cacheId: CacheId,
      mdn,
      customerName: UserName,
      AgreementFailedAttempt: agreementFailedAttempt,
    })
  ).catch((err) => dispatch(serviceUnavailable()));

  // not allowing to go ahead if response is empty
  if (isEmpty(response)) {
    return;
  }

  // After API Call
  await dispatch(
    updateApiProgress(API_PROGRESS.ASK_MDN_SUCCESS, 100, COMPONENTS.ASK_MDN, 40)
  );
  const {
    Agreement: { AgreementFound, AgreementFailedAttempt, AgreementStatus, ClientProductSkuNbr },
  } = response;
  await sleep(1000);
  await dispatch(clearApiProgress());

  const isSingtelStarterTerminated = isSingtel && (ClientProductSkuNbr && ClientProductSkuNbr.toLowerCase().indexOf("starter") > -1 &&
  AgreementStatus === "TRMNTD")

  if (AgreementFound || isSingtelStarterTerminated) {
    dispatch(updateVisitor({ mdn, lastActivity: ACTIVITY.ASK_MDN }));
    const initial = getStore().journeyMessages.flowProgress.percentage;
    await dispatch(
      updateFlowProgress(
        PROGRESS_DATA.ASK_NAME.title,
        PROGRESS_DATA.ASK_NAME.count,
        initial
      )
    );
    dispatch(
      addJourneyMessages([
        createMessage("TEXT", "system", { key: "SystemMessage.AskFullName" }),
        createMessage("VALIDATION", "system", { showComponent: isSingtelStarterTerminated ? "AskNric" : "AskName" }),
      ])
    );
  } else {
    dispatch(
      addJourneyMessages([
        createMessage("VALIDATION", "system", {
          showComponent: "IncorrectMdn",
        }),
      ])
    );
    if (AgreementFailedAttempt < 3) {
      dispatch(
        addJourneyMessages([
          createMessage("VALIDATION", "system", {
            showComponent: "ReVerifyMdn",
          }),
        ])
      );
    }
  }
};

export const validateName = (firstName, lastName) => async (
  dispatch,
  getStore
) => {
  // Before API Call
  const customerName = `${firstName} ${lastName}`;
  const { CacheId } = getStore().session.sessionData.Configurations;
  const {
    agreement: { NameFailedAttempt: nameFailedAttempt },
    inputData: { mdn },
  } = getStore().validation;
  dispatch(addJourneyMessages([createMessage("TEXT", "user", customerName)]));
  await dispatch(setNamesData(firstName, lastName));
  await dispatch(
    updateApiProgress(API_PROGRESS.ASK_NAME, 40, COMPONENTS.ASK_NAME)
  );

  // API Call
  const response = await dispatch(
    agreementsApi({
      mdn,
      cacheId: CacheId,
      firstName,
      lastName,
      customerName,
      NameFailedAttempt: nameFailedAttempt,
    })
  ).catch((err) => dispatch(serviceUnavailable()));

  // not allowing to go ahead if response is empty
  if (isEmpty(response)) {
    return;
  }

  // After API Call
  await dispatch(
    updateApiProgress(
      API_PROGRESS.ASK_NAME_SUCCESS,
      100,
      COMPONENTS.ASK_NAME,
      40
    )
  );
  await sleep(1000);
  await dispatch(clearApiProgress());
  const {
    Agreement: { NameVerified, NameFailedAttempt },
  } = response;
  if (NameVerified) {
    dispatch(
      updateVisitor({ userName: customerName, lastActivity: ACTIVITY.ASK_NAME })
    );
    const initial = getStore().journeyMessages.flowProgress.percentage;
    await dispatch(
      updateFlowProgress(
        PROGRESS_DATA.ASK_NRIC.title,
        PROGRESS_DATA.ASK_NRIC.count,
        initial
      )
    );
    dispatch(
      addJourneyMessages([
        createMessage("TEXT", "system", {
          key: "SystemMessage.AskForNric",
          values: { firstName },
        }),
        createMessage("VALIDATION", "system", { showComponent: "AskNric" }),
      ])
    );
  } else {
    dispatch(
      addJourneyMessages([
        createMessage("VALIDATION", "system", {
          showComponent: "IncorrectName",
        }),
      ])
    );
    if (NameFailedAttempt < 4) {
      dispatch(
        addJourneyMessages([
          createMessage("VALIDATION", "system", { showComponent: "AskName" }),
        ])
      );
    } else {
      await dispatch(setChatReason("Invalid Name"));
      dispatch(udpateChatInputVisibility(true));
    }
  }
};

export const validateNric = (nricCode, isAgentVerified = false) => async (
  dispatch,
  getStore
) => {
  // Before API Call
  const { CacheId } = getStore().session.sessionData.Configurations;
  const {
    verification,
    inputData: { mdn },
  } = getStore().validation;
  let { VerificationFailedAttempt: verificationFailedAttempt } = !isEmpty(
    verification
  )
    ? verification
    : {};
  if (nricCode !== "AGENT_VERIFIED") {
    const maskedCode = getMaskedNric(nricCode);
    dispatch(addJourneyMessages([createMessage("TEXT", "user", maskedCode)]));
  }
  await dispatch(setNricData(nricCode));
  await dispatch(
    updateApiProgress(API_PROGRESS.ASK_NRIC, 40, COMPONENTS.ASK_NRIC)
  );

  // check agent verified
  // verificationFailedAttempt = isAgentVerified ? 0 : verificationFailedAttempt;

  const client = getStore().app.client;
  const isSingtel = client === CLIENT.SINGTEL;
  const ClientProductSkuNbr = getStore().validation?.agreement?.ClientProductSkuNbr;
  const isSingtelStarter = isSingtel && (ClientProductSkuNbr && ClientProductSkuNbr.toLowerCase().indexOf("starter") > -1)

  // API Call
  const response = await dispatch(
    verifyNricApi({
      mdn,
      cacheId: CacheId,
      verificationFailedAttempt,
      nricCode,
      isAgentVerified,
      // isSingtelStarter: isSingtelStarter,
      isSingtelStarter: true,
    })
  ).catch((err) => dispatch(serviceUnavailable()));

  // not allowing to go ahead if response is empty
  if (isEmpty(response)) {
    return;
  }

  await dispatch(
    updateApiProgress(
      API_PROGRESS.ASK_NRIC_SUCCESS,
      100,
      COMPONENTS.ASK_NRIC,
      40
    )
  );
  await sleep(1000);
  await dispatch(clearApiProgress());

  // After API Call
  const {
    VerificationOutcome: {
      VerifyCustomerResults: { VerificationOutcome },
      VerificationFailedAttempt,
    },
  } = response;
  if (VerificationOutcome === "Verified") {
    dispatch(updateVisitor({ lastActivity: ACTIVITY.ASK_NRIC }));
    const initial = getStore().journeyMessages.flowProgress.percentage;

  dispatch(
    updateFlowProgress(
      PROGRESS_DATA.CONFIRM_DEVICE.title,
      PROGRESS_DATA.CONFIRM_DEVICE.count,
      initial
    )
  );
  dispatch(
    addJourneyMessages([
      createMessage("DEVICE_CONFIRMATION", "system", {
        showComponent: "deviceList",
      }),
    ])
  );
  } else {
    await dispatch(
      updateApiProgress(
        API_PROGRESS.ASK_NRIC_SUCCESS,
        100,
        COMPONENTS.ASK_NRIC,
        40
      )
    );
    await sleep(1000);
    await dispatch(clearApiProgress());
    dispatch(
      addJourneyMessages([
        createMessage("VALIDATION", "system", {
          showComponent: "IncorrectNric",
        }),
      ])
    );
    if (VerificationFailedAttempt < 2) {
      dispatch(
        addJourneyMessages([
          createMessage("VALIDATION", "system", { showComponent: "AskNric" }),
        ])
      );
    } else {
      await dispatch(setChatReason("Invalid Nric"));
      dispatch(udpateChatInputVisibility(true));
    }
  }
};
